import React, { useState, useEffect, useRef } from 'react';
import { motion, useInView } from 'motion/react';
import { Link } from 'react-router-dom';
import { Search, Clock, Eye, ArrowRight, Filter, User, Star, Layers, Tag } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import SEOHead from '../components/SEOHead';
import Layout from '../components/layout/Layout';
import LazyImage from '../components/ui/LazyImage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Text Reveal Animation
const TextReveal = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 60 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
};

// Blog Card Component
const BlogCard = ({ post, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-30px" });

  const imageUrl = post.featured_image || 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80';
  const readTime = post.read_time || 5;
  const categoryDisplay = post.category?.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Blog';

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.6, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
    >
      <Link to={`/blog/${post.slug}`} data-testid={`blog-card-${post.slug}`}>
        <motion.article
          className="group card-fresh overflow-hidden"
          whileHover={{ y: -8 }}
        >
          {/* Image */}
          <div className="relative h-48 -mx-8 -mt-8 mb-6 overflow-hidden">
            <LazyImage src={post.featured_image} alt={post.title} className="w-full h-full group-hover:scale-110 transition-transform duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent" />
            
            {/* Badges */}
            <div className="absolute top-4 left-4 flex items-center gap-2">
              <span className="px-3 py-1 bg-gradient-to-r from-[#FF6B35] to-[#FFB627] text-white text-xs font-semibold rounded-full">
                {categoryDisplay}
              </span>
              {post.is_featured && (
                <span className="px-2 py-1 bg-amber-500 text-white text-xs font-semibold rounded-full flex items-center gap-1">
                  <Star size={10} fill="currentColor" /> Featured
                </span>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="relative z-10">
            <h3 className="font-display text-xl font-semibold text-[#2D2D2D] mb-3 group-hover:text-[#FF6B35] transition-colors line-clamp-2">
              {post.title}
            </h3>
            
            {/* Excerpt */}
            {post.excerpt && (
              <p className="text-[#5A5A5A] text-sm mb-4 line-clamp-2">{post.excerpt}</p>
            )}
            
            {/* Meta */}
            <div className="flex items-center gap-4 text-[#8A8A8A] text-sm mb-4">
              <span className="flex items-center gap-1">
                <Clock size={14} />
                {readTime} min
              </span>
              <span className="flex items-center gap-1">
                <Eye size={14} />
                {post.views || 0}
              </span>
            </div>

            {/* Author */}
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-[#5A5A5A] text-sm">
                <User size={14} />
                {post.author_name || 'Buzz Craft Maven Team'}
              </span>
              <ArrowRight size={18} className="text-[#8A8A8A] group-hover:text-[#FF6B35] group-hover:translate-x-1 transition-all" />
            </div>
          </div>
        </motion.article>
      </Link>
    </motion.div>
  );
};

const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [totalPosts, setTotalPosts] = useState(0);

  useEffect(() => {
    fetchPosts();
    fetchCategories();
    fetchTags();
    fetchAuthors();
  }, [selectedCategory]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const categoryParam = selectedCategory !== 'all' ? `&category=${selectedCategory}` : '';
      const response = await fetch(`${API_URL}/api/public/blog/posts?limit=50${categoryParam}`);
      const data = await response.json();
      setPosts(data.posts || []);
      setTotalPosts(data.total || 0);
    } catch (error) {
      console.error('Error fetching posts:', error);
      // Fallback to old API
      try {
        const response = await fetch(`${API_URL}/api/blog/posts?limit=50`);
        const data = await response.json();
        setPosts(data);
        setTotalPosts(data.length);
      } catch (err) {
        console.error('Fallback also failed:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/public/blog/categories`);
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await fetch(`${API_URL}/api/public/blog/tags`);
      const data = await response.json();
      setTags(data);
    } catch (error) {
      console.error('Error fetching tags:', error);
    }
  };

  const fetchAuthors = async () => {
    try {
      const response = await fetch(`${API_URL}/api/public/blog/authors`);
      const data = await response.json();
      setAuthors(data);
    } catch (error) {
      console.error('Error fetching authors:', error);
    }
  };

  const filteredPosts = posts.filter(post => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return post.title?.toLowerCase().includes(query) ||
           post.excerpt?.toLowerCase().includes(query) ||
           post.keywords?.some(k => k.toLowerCase().includes(query));
  });

  // Stats
  const totalViews = posts.reduce((acc, post) => acc + (post.views || 0), 0);

  return (
    <Layout>
      <SEOHead
        title="Insights | Buzz Craft Maven - Digital Marketing & Interior Design"
        description="Expert insights on digital marketing, brand strategy, interior design, and business growth from Buzz Craft Maven. Actionable strategies for HNI, founders, and modern businesses."
        canonical="https://buzzcraftmaven.com/blog"
        ogTitle="Insights | Buzz Craft Maven"
        ogDescription="Expert insights on digital marketing, brand strategy, interior design, and business growth."
        schemas={[
          { "@context": "https://schema.org", "@type": "CollectionPage", "name": "Buzz Craft Maven Insights", "url": "https://buzzcraftmaven.com/blog", "publisher": { "@type": "Organization", "name": "Buzz Craft Maven" } },
          { "@context": "https://schema.org", "@type": "BreadcrumbList", "itemListElement": [{ "@type": "ListItem", "position": 1, "name": "Home", "item": "https://buzzcraftmaven.com" }, { "@type": "ListItem", "position": 2, "name": "Insights", "item": "https://buzzcraftmaven.com/blog" }] }
        ]}
      />

      {/* HERO SECTION */}
      <section className="pt-32 pb-12 md:pt-40 md:pb-16 bg-sunrise-hero relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <motion.div 
            className="absolute top-20 right-[15%] w-[400px] h-[400px] sun-orb sun-orb-primary"
            animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.5, 0.3] }}
            transition={{ duration: 8, repeat: Infinity }}
          />
        </div>

        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <nav className="text-sm text-[#8A8A8A] mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#FF6B35]">Home</Link> <span className="mx-2">/</span> <span className="text-[#2D2D2D]">Insights</span>
          </nav>
          <div className="grid grid-cols-12 gap-8 items-end">
            {/* Left - Content */}
            <div className="col-span-12 lg:col-span-7">
              <TextReveal>
                <span className="inline-block px-4 py-1.5 bg-[#FF6B35]/10 text-[#FF6B35] text-sm font-medium rounded-full mb-6">Knowledge Hub</span>
              </TextReveal>
              
              <TextReveal delay={0.1}>
                <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-[#2D2D2D] leading-[1.05] mb-4">
                  Insights for
                </h1>
              </TextReveal>
              <TextReveal delay={0.15}>
                <h1 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] mb-8">
                  <span className="text-gradient-sunrise">growth & creativity.</span>
                </h1>
              </TextReveal>
              
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-lg text-[#5A5A5A] max-w-xl"
              >
                Expert strategies and insights across our verticals: Digital Marketing, 
                Interior Design, Photography & Web Development.
              </motion.p>
            </div>

            {/* Right - Stats */}
            <div className="col-span-12 lg:col-span-5">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="flex items-center gap-8 lg:justify-end"
              >
                <div className="text-center">
                  <p className="font-display text-3xl text-[#FF6B35] font-bold">{totalPosts}</p>
                  <p className="text-[#8A8A8A] text-sm">Articles</p>
                </div>
                <div className="w-px h-10 bg-[#FF6B35]/20" />
                <div className="text-center">
                  <p className="font-display text-3xl text-[#FF6B35] font-bold">{totalViews}</p>
                  <p className="text-[#8A8A8A] text-sm">Views</p>
                </div>
                <div className="w-px h-10 bg-[#FF6B35]/20" />
                <div className="text-center">
                  <p className="font-display text-3xl text-[#FF6B35] font-bold">{categories.length}</p>
                  <p className="text-[#8A8A8A] text-sm">Categories</p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* FILTERS */}
      <section className="py-6 bg-white border-y border-[#FF6B35]/10 sticky top-20 z-40">
        <div className="container mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8A8A8A]" size={18} />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-testid="blog-search"
                className="w-full pl-12 pr-4 py-3 bg-[#FFFBF5] border border-[#FF6B35]/15 rounded-full text-[#2D2D2D] placeholder-[#8A8A8A] focus:border-[#FF6B35] focus:outline-none transition-colors"
              />
            </div>

            {/* Categories */}
            <div className="flex items-center gap-2 flex-wrap">
              <Filter size={16} className="text-[#8A8A8A] mr-2" />
              <button
                onClick={() => setSelectedCategory('all')}
                data-testid="filter-all"
                className={`px-4 py-2 rounded-full text-sm transition-all ${
                  selectedCategory === 'all'
                    ? 'bg-gradient-to-r from-[#FF6B35] to-[#FFB627] text-white font-medium shadow-lg shadow-orange-500/20'
                    : 'bg-[#FFFBF5] text-[#5A5A5A] border border-[#FF6B35]/15 hover:border-[#FF6B35]/30'
                }`}
              >
                All
              </button>
              {categories.map((category) => (
                <button
                  key={category.slug}
                  onClick={() => setSelectedCategory(category.slug)}
                  data-testid={`filter-${category.slug}`}
                  className={`px-4 py-2 rounded-full text-sm transition-all ${
                    selectedCategory === category.slug
                      ? 'bg-gradient-to-r from-[#FF6B35] to-[#FFB627] text-white font-medium shadow-lg shadow-orange-500/20'
                      : 'bg-[#FFFBF5] text-[#5A5A5A] border border-[#FF6B35]/15 hover:border-[#FF6B35]/30'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* BLOG GRID */}
      <section className="section-padding bg-[#FFFBF5]">
        <div className="container mx-auto px-6 md:px-12">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="card-fresh animate-pulse">
                  <div className="h-48 -mx-8 -mt-8 mb-6 bg-[#FF6B35]/5 rounded-t-2xl" />
                  <div className="h-6 bg-[#FF6B35]/5 rounded mb-3" />
                  <div className="h-4 bg-[#FF6B35]/5 rounded w-2/3" />
                </div>
              ))}
            </div>
          ) : filteredPosts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPosts.map((post, index) => (
                <BlogCard key={post.id} post={post} index={index} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-[#5A5A5A] text-lg">No articles found matching your search.</p>
            </div>
          )}
        </div>
      </section>

      {/* DISCOVER SECTION - Tags, Categories, Authors */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Browse by Category */}
            {categories.length > 0 && (
              <div>
                <h3 className="font-display text-xl font-semibold text-[#2D2D2D] mb-6 flex items-center gap-2">
                  <Layers size={20} className="text-[#FF6B35]" /> Browse by Category
                </h3>
                <div className="space-y-2">
                  {categories.map(cat => (
                    <Link key={cat.slug} to={`/blog/category/${cat.slug}`} data-testid={`discover-category-${cat.slug}`}
                      className="flex items-center justify-between p-3 rounded-xl hover:bg-[#FFFBF5] border border-transparent hover:border-[#FF6B35]/10 transition-all group">
                      <span className="text-[#2D2D2D] group-hover:text-[#FF6B35] transition-colors">{cat.name}</span>
                      <span className="text-[#8A8A8A] text-sm">{cat.post_count} posts</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Popular Tags */}
            {tags.length > 0 && (
              <div>
                <h3 className="font-display text-xl font-semibold text-[#2D2D2D] mb-6 flex items-center gap-2">
                  <Tag size={20} className="text-[#FF6B35]" /> Popular Tags
                </h3>
                <div className="flex flex-wrap gap-2">
                  {tags.slice(0, 15).map(t => (
                    <Link key={t.slug} to={`/blog/tag/${t.slug}`} data-testid={`discover-tag-${t.slug}`}
                      className="px-4 py-2 bg-[#FFFBF5] text-[#5A5A5A] text-sm rounded-full border border-[#FF6B35]/10 hover:border-[#FF6B35]/30 hover:text-[#FF6B35] transition-all">
                      #{t.name} <span className="text-[#8A8A8A] ml-1">({t.post_count})</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Authors */}
            {authors.length > 0 && (
              <div>
                <h3 className="font-display text-xl font-semibold text-[#2D2D2D] mb-6 flex items-center gap-2">
                  <User size={20} className="text-[#FF6B35]" /> Our Authors
                </h3>
                <div className="space-y-3">
                  {authors.map(author => (
                    <Link key={author.id} to={`/blog/author/${author.slug}`} data-testid={`discover-author-${author.slug}`}
                      className="flex items-center gap-4 p-3 rounded-xl hover:bg-[#FFFBF5] border border-transparent hover:border-[#FF6B35]/10 transition-all group">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#FFB627] flex items-center justify-center text-white font-display font-bold flex-shrink-0">
                        {author.name?.charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[#2D2D2D] font-medium group-hover:text-[#FF6B35] transition-colors truncate">{author.name}</p>
                        <p className="text-[#8A8A8A] text-sm">{author.articles_count || 0} articles</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Blog;
